////////////////////////////////////////
// IMPORTS - Analysis Page Dependencies
////////////////////////////////////////
import React, { useState, useEffect, useContext, createContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import PricingCurvePage from './PricingCurvePage';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist';
import ErrorMessagePopup from '../CommanService/ErrorMessagePopup';
import Loader from '../CommanService/Loader';
import { apiCallWithCache } from '../utils/apiUtils';
import sliderIcon from '../assets/Icon/slider_icon.svg';
import Select from 'react-select';
import './AnalysisPage.css';
import './R application/CustomSelect.css';
const Plot = createPlotlyComponent(Plotly);

////////////////////////////////////////
// CONTEXT DEFINITIONS - Analysis Page State Management
////////////////////////////////////////
export const AnalysisSelectionContext = createContext({
  selectedRegion: '',
  selectedInterchange: '',
  selectedPartType: 'ENGINE',
  showSoldParts: true,
  includeBrokered: true
});

export const AnalysisFilteredInventoryContext = createContext({ 
  filteredInventory: [], 
  setFilteredInventory: () => {} 
});

export const AnalysisFilteredSalesContext = createContext([]);
export const AnalysisAllSalesDataContext = createContext([]);
export const AnalysisAllInventoryDataContext = createContext([]);
export const AnalysisSuggestedPriceContext = createContext({ 
  suggestedPrice: null, 
  setSuggestedPrice: () => {} 
});

////////////////////////////////////////
// MAIN COMPONENT - Analysis Page Controller
////////////////////////////////////////
const AnalysisPage = () => {
  const navigate = useNavigate();
  
  // State management
  const priceLinesSetFromHistory = useRef(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [interchanges, setInterchanges] = useState([]);
  const [selectedInterchange, setSelectedInterchange] = useState('');
  const [selectedPartType, setSelectedPartType] = useState('ENGINE');
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [allSalesData, setAllSalesData] = useState([]);
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [enginePricingData, setEnginePricingata] = useState([]);
  const [transmissionPricingData, setTransmissionPricingData] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [showSoldParts, setShowSoldParts] = useState(true);
  const [includeBrokered, setIncludeBrokered] = useState(true);
  const [moveToPriceLine, setMoveToPriceLine] = useState(false);
  const [additionalPriceLine, setAdditionalPriceLine] = useState(0);
  const [userMileage, setUserMileage] = useState('');
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  
  // Price line configuration
  const [priceLine1, setPriceLine1] = useState(0);
  const [priceLine2, setPriceLine2] = useState(0);
  const [priceLine3, setPriceLine3] = useState(0);
  const [priceLine4, setPriceLine4] = useState(0);
  
  // Analysis search functionality
  const [allAnalysis, setAllAnalysis] = useState([]);
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showAnalysisDropdown, setShowAnalysisDropdown] = useState(false);
  
  // Interchange search functionality
  const [interchangeSearch, setInterchangeSearch] = useState('');
  
  // Interchange change handler (same as Model Exploration)
  const handleInterchangeChange = (opt) => {
    setSelectedInterchange(opt ? opt.value : '');
  };
  
  // Tab management
  const [activeTab, setActiveTab] = useState('main');
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  
  // Loading states
  const [enginePricingLoading, setEnginePricingLoading] = useState(true);
  const [transmissionPricingLoading, setTransmissionPricingLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);
  const [priceLineConfigLoading, setPriceLineConfigLoading] = useState(true);
  const [controlsHistoryLoading, setControlsHistoryLoading] = useState(true);
  
  // Additional data states
  const [allStockData, setAllStockData] = useState([]); 
  const [priceLineConfig, setPriceLineConfig] = useState([]);

  // // Graph data (same source as Override page)
  // const [overrideGraphData, setOverrideGraphData] = useState(null);
  // // Override dropdown sources
  // const [overrideRegions, setOverrideRegions] = useState([]);
  // const [overrideInterchanges, setOverrideInterchanges] = useState([]);
  // const [overridePartTypes, setOverridePartTypes] = useState([]);
  
  // Dashboard card data states
  const [stockStatus, setStockStatus] = useState('');
  const [inStockCount, setInStockCount] = useState(0);
  const [targetQOH, setTargetQOH] = useState(0);
  const [instockPartsNeeded, setInstockPartsNeeded] = useState(0);
  const [selectedStockData, setSelectedStockData] = useState(null);
  
  // Popup states
  const [popupState, setPopupState] = useState({
    isVisible: false,
    message: '',
    subMessage: '',
    popupType: 'error',
    customPosition: 'validation'
  });
  
  const closePopup = () => setPopupState(prev => ({ ...prev, isVisible: false }));

  // Load all data on component mount
  useEffect(() => {
    loadAllData();
    // loadUserAnalysisData();
  }, []);
  // Update price line values based on stock data and price line config (same logic as Model Exploration)
  useEffect(() => {
    populateGlobalConfig();
  }, [selectedRegion, selectedInterchange, allStockData, priceLineConfig, selectedPartType]);

  useEffect(() => {
    if (allStockData.length && enginePricingData.length && transmissionPricingData.length && priceLineConfig.length) {
      checkAndPopulateControlsFromHistory();
    }
  }, [allStockData, enginePricingData, transmissionPricingData, priceLineConfig]);
  
  // Console log selected data when part type, region, or interchange changes
  useEffect(() => {      
    let uniqueRegions = [];
    // let uniqueInterchanges = [];
  
    if (selectedPartType === 'ENGINE' && enginePricingData) {
      uniqueRegions = [...new Set(enginePricingData.map(item => item.region))];
      // uniqueInterchanges = [...new Set(enginePricingData.filter(item => item.region === selectedRegion) .map(item => item.interchange_number))];
    } else if (selectedPartType !== 'ENGINE' && transmissionPricingData) {
      uniqueRegions = [...new Set(transmissionPricingData.map(item => item.region))];
      // uniqueInterchanges = [...new Set(transmissionPricingData.filter(item => item.region === selectedRegion) .map(item => item.interchange_number))];
    }
  
    setRegions(uniqueRegions);
    // setInterchanges(uniqueInterchanges);
  
    if (uniqueRegions.length > 0) {
      setSelectedRegion(uniqueRegions[0]);
    }

    console.log('Unique Regions:', uniqueRegions);
    // console.log('Unique Interchanges:', uniqueInterchanges);
    // console.log('Selected Region:', selectedRegion);
    // console.log('Selected Interchange:', selectedInterchange);
    // console.log('Selected Part Type:', selectedPartType);
    // console.log('Engine Pricing Data:', enginePricingData);
    // console.log('Transmission Pricing Data:', transmissionPricingData);
  }, [selectedPartType, enginePricingData, transmissionPricingData]);
  
  useEffect(() => {      
    // let uniqueRegions = [];
    let uniqueInterchanges = [];
  
    if (selectedPartType === 'ENGINE' && enginePricingData) {
      // uniqueRegions = [...new Set(enginePricingData.map(item => item.region))];
      uniqueInterchanges = [...new Set(enginePricingData.filter(item => item.region === selectedRegion).map(item => item.interchange_number))];
    } else if (selectedPartType !== 'ENGINE' && transmissionPricingData) {
      // uniqueRegions = [...new Set(transmissionPricingData.map(item => item.region))];
      uniqueInterchanges = [...new Set(transmissionPricingData.filter(item => item.region === selectedRegion).map(item => item.interchange_number))];
    }
  
    // setRegions(uniqueRegions);
    setInterchanges(uniqueInterchanges);
  
    // if (uniqueRegions.length > 0) {
    //   setSelectedRegion(uniqueRegions[0]);
    // }
    console.log('Unique Interchanges:', uniqueInterchanges);

    // console.log('Unique Regions:', uniqueRegions);
    // console.log('Unique Interchanges:', uniqueInterchanges);
    // console.log('Selected Region:', selectedRegion);
    // console.log('Selected Interchange:', selectedInterchange);
    // console.log('Selected Part Type:', selectedPartType);
    // console.log('Engine Pricing Data:', enginePricingData);
    console.log('Transmission Pricing Data:', transmissionPricingData);
  }, [selectedRegion, enginePricingData, transmissionPricingData]);
  
  ////////////////////////////////////////
  // CONTROLS HISTORY FUNCTIONS
  ////////////////////////////////////////

  const clearControlsHistory = async () => {
  populateGlobalConfig();
  };
  /**
   * Save Controls History Function
   * Purpose: Save current control values and stock status to controls_history table
   */
  const saveControlsHistory = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const userId = user?.id || 'Not available';
      const userName = user?.username || user?.first_name + ' ' + user?.last_name || 'Unknown User';

      const controlsHistoryData = {
        part_type: selectedPartType,
        region: selectedRegion,
        interchange: selectedInterchange,
        show_sales: showSoldParts,
        include_brokered: includeBrokered,
        user_mileage: parseInt(userMileage) || null,
        additional_price_line_percent: parseFloat(additionalPriceLine) || null,
        move_to_price_line: moveToPriceLine,
        price_line_q1: parseFloat(priceLine1) || null,
        price_line_q2: parseFloat(priceLine2) || null,
        price_line_q3: parseFloat(priceLine3) || null,
        price_line_q4: parseFloat(priceLine4) || null,
        filter_name: `Analysis_${selectedPartType}_${selectedRegion}_${selectedInterchange}`,
        p_user_name: userName,
        stock_status: stockStatus
      };

      console.log('Saving controls history:', controlsHistoryData);

      const response = await apiService.SaveControlsHistory(controlsHistoryData);
      
      if (response.success) {
        console.log('Controls history saved successfully:', response);
        setPopupState({
          isVisible: true,
          message: 'Controls saved successfully!',
          subMessage: 'Your analysis settings have been saved.',
          popupType: 'success',
          customPosition: 'validation'
        });
      } else {
        console.error('Failed to save controls history:', response.message);
        setPopupState({
          isVisible: true,
          message: 'Failed to save controls',
          subMessage: response.message || 'Please try again.',
          popupType: 'error',
          customPosition: 'validation'
        });
      }
    } catch (error) {
      console.error('Error saving controls history:', error);
      setPopupState({
        isVisible: true,
        message: 'Error saving controls',
        subMessage: error.message || 'Please try again.',
        popupType: 'error',
        customPosition: 'validation'
      });
    }
  };

  /**
   * Check and Populate Controls from History
   * Purpose: Check if current stock status matches saved history and populate controls
   */
  const checkAndPopulateControlsFromHistory = async () => {
    try {
      console.log('Checking for matching stock status:');

      // Step 1: Get the last controls history
      const response = await apiService.GetLastControlsHistoryByStockStatus();

      const matchingStock = allStockData.find(item => 
        item.region === response.data.region && 
        item.interchange_number === response.data.interchange &&
        item.part_name === response.data.part_Type
      );
 
      if (response.success && response.data) {
        console.log('Found controls history:', response.data);
        
        // Step 2: Get the current stock status for the part/region/interchange from history
        // const stockResponse = await apiService.GetStockStatus({
        //   partType: response.data.part_type,
        //   region: response.data.region,
        //   interchange: response.data.interchange
        // });
        
        // Populate controls from the saved history
        setSelectedPartType(response.data.part_Type.toString() || '');
        setSelectedRegion(response.data.region.toString() || '');
        setSelectedInterchange(response.data.interchange?.toString() || '');
        
        // Step 3: Only populate controls if stock status matches
        if (response.data.stock_Status === matchingStock.stock_status) {
          console.log('Stock status matches, populating controls');
          
          
          setShowSoldParts(response.data.show_Sales || true);
          setIncludeBrokered(response.data.include_Brokered || true);
          setUserMileage(response.data.user_Mileage?.toString() || '');
          setAdditionalPriceLine(response.data.additional_Price_Line_Percent?.toString() || '0');
          setMoveToPriceLine(response.data.move_To_Price_Line || false);
          // Set price lines from history and mark them as set
          setPriceLine1(response.data.price_Line_Q1?.toString() || 0);
          setPriceLine2(response.data.price_Line_Q2?.toString() || 0);
          setPriceLine3(response.data.price_Line_Q3?.toString() || 0);
          setPriceLine4(response.data.price_Line_Q4?.toString() || 0);
          priceLinesSetFromHistory.current = true;
          
          // Update the stock status in state
          setStockStatus(response.data.stock_Status);

          console.log('Controls populated from history successfully');
          
          // setPopupState({
          //   isVisible: true,
          //   message: 'Controls Fetched Successfully',
          //   subMessage: ``,//`Found matching analysis for ${matchingStock.stock_status} stock status.`,
          //   popupType: 'success',
          //   customPosition: 'validation'
          // });
        } else {
          console.log('Stock status does not match, populating global config to controls');
          populateGlobalConfig();
        }
      } else {
        console.log('No controls history found');
      }
    } catch (error) {
      console.error('Error checking controls history:', error);
      // Don't show error popup for this check as it's automatic
    } finally {
      setControlsHistoryLoading(false);
    }
  };

  ////////////////////////////////////////
  // API CALLS - All Data Loading Functions
  ////////////////////////////////////////
  
  /**
   * API Call 1: Get All Engine Pricing Data
   * Purpose: Load engine pricing data and extract unique regions for dropdown
   * Endpoint: GetAllEnginePricing()
   * Data Used: Sets regions list and selects first region by default
   */

  const loadEnginePricingData = async () => {
    try {
      const enginePricingResult = await apiCallWithCache('enginePricing', () => apiService.GetAllEnginePricing());
      if (enginePricingResult?.data) {
        const uniqueRegions = [...new Set(enginePricingResult.data.map(item => item.region))];
        const uniqueInterchanges = [...new Set(enginePricingResult.data.map(item => item.interchange_number))];

        setEnginePricingata(enginePricingResult.data);
        setInterchanges(uniqueInterchanges);
        setRegions(uniqueRegions);
        if (uniqueRegions.length > 0) {
          setSelectedRegion(uniqueRegions[0]);
        }
        loadTransmissionPricingData();
      }
    } catch (error) {
      console.error('Failed to load engine pricing:', error);
    } finally {
      setEnginePricingLoading(false);
    }
  };

  /**
   * API Call 1b: Get All Transmission Pricing Data
   * Purpose: Ensure transmission pricing endpoint is invoked after engine pricing
   * Endpoint: GetAllTransmissionPricing()
   * Note: Backend engine pricing already merges transmission; this call is for explicit fetch when needed.
   */
  const loadTransmissionPricingData = async () => {
    try {
      const transmissionPricingResult = await apiCallWithCache('transmissionPricing', () => apiService.GetAllTransmissionPricing());
      if (transmissionPricingResult?.data) {
        const uniqueRegions = [...new Set(transmissionPricingResult.data.map(item => item.region))];
        setTransmissionPricingData(transmissionPricingResult.data);
        // if (uniqueRegions.length > 0) {
        //   setSelectedRegion(uniqueRegions[0]);
        // }
        loadSalesData();
      }
    } catch (error) {
      console.error('Failed to load transmission pricing:', error);
    } finally {
      setTransmissionPricingLoading(false);
    }
  };

  
  /**
   * API Call 2: Get All Sales Data
   * Purpose: Load sales data and transform mileage values (multiply by 1000)
   * Endpoint: GetAllsales()
   * Data Used: Sets allSalesData for filtering and analysis
   */
  const loadSalesData = async () => {
    try {
      const salesResult = await apiCallWithCache('sales', () => apiService.GetAllsales());
      if (salesResult?.data) {
        let salesResultantData = salesResult.data.map(item => ({
          ...item,
          mileage: item.mileage * 1000
        }));
        setAllSalesData(salesResultantData);
        loadStockData();
      }
    } catch (error) {
      console.error('Failed to load sales:', error);
    } finally {
      setSalesLoading(false);
    }
  };

  /**
   * API Call 3: Get All Inventory Data
   * Purpose: Load inventory data for filtering and analysis
   * Endpoint: GetAllInventory()
   * Data Used: Sets allInventoryData for filtering by region and interchange
   */
  const loadInventoryData = async () => {
    try {
      const inventoryResult = await apiCallWithCache('inventory', () => apiService.GetAllInventory());
      if (inventoryResult?.data) {
        setAllInventoryData(inventoryResult.data);
        loadPriceLineConfig();
      }
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setInventoryLoading(false);
    }
  };

  /**
   * API Call 4: Get All Stock Data
   * Purpose: Load stock data for price line calculation
   * Endpoint: GetAllStock()
   * Data Used: Sets allStockData for price line configuration
   */
  const loadStockData = async () => {
    try {
      const stockResult = await apiCallWithCache('stock', () => apiService.GetAllStock());
      if (stockResult?.data) {
        setAllStockData(stockResult.data);
        loadInventoryData();
      }
    } catch (error) {
      console.error('Failed to load stock:', error);
    } finally {
      setStockLoading(false);
    }
  };

  /**
   * API Call 5: Get Price Line Configuration
   * Purpose: Load price line configuration for Q1, Q2, Q3, Q4 calculation
   * Endpoint: GetPriceLineConfig()
   * Data Used: Sets priceLineConfig for dynamic price line values
   */
  const loadPriceLineConfig = async () => {
    try {
      const priceLineConfigResult = await apiCallWithCache('priceLineConfig', () => apiService.GetPriceLineConfig());
      if (priceLineConfigResult?.data) {
        setPriceLineConfig(priceLineConfigResult.data);
        
        // apiService.GetBatchGraphData();
        // apiService.GetFinalisedData();
      }
    } catch (error) {
      console.error('Failed to load price line config:', error);
    } finally {
      setPriceLineConfigLoading(false);
    }
  };

  /**
   * API Call 6: Get All Analysis by User ID
   * Purpose: Load user's saved analysis for search functionality
   * Endpoint: GetAllAnalysisBYUSerID(userId)
   * Data Used: Sets allAnalysis for search dropdown and analysis selection
   */
  const loadUserAnalysisData = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const userId = user?.id || 'Not available';
      const response = await apiService.GetAllAnalysisBYUSerID(userId);
      setAllAnalysis(response.data);
    } catch (error) {
      console.error('Failed to load user analysis:', error);
    }
  };

  /**
   * Main Data Loading Function
   * Purpose: Orchestrates all API calls and manages loading states
   * Calls: All individual API functions in parallel for better performance
   */
  const loadAllData = async () => {
    try {
      console.log('Loading Analysis page data...');
      
      // Load engine pricing first, then explicitly call transmission pricing
      
      // Execute remaining API calls in parallel for better performance
      const results = await Promise.all([
        loadEnginePricingData(),
        // loadTransmissionPricingData(),
        // loadSalesData(),
        // loadStockData(),
        // loadInventoryData(),
        // loadPriceLineConfig(),
        // loadUserAnalysisData(),
        // apiService.GetBatchGraphData(),
        // apiService.GetFinalisedData()
      ]);

      // Store override graph payload
      // const graphPayload = [];//results[results.length - 2];
      // if (graphPayload && graphPayload.data) {
      //   setOverrideGraphData(graphPayload.data);
      // } else if (graphPayload && (graphPayload.inventory_pe || graphPayload.sales)) {
      //   setOverrideGraphData(graphPayload);
      // }

      // // Populate dropdown sources from FinalisedData (same as Override)
      // const finalisedResp = [];//results[results.length - 1];
      // const finalisedData = [];//finalisedResp?.data || [];
      // if (Array.isArray(finalisedData) && finalisedData.length > 0) {
      //   const uniqueRegions = [...new Set(finalisedData.map(item => item.region).filter(Boolean))];
      //   const uniqueInterchanges = [...new Set(finalisedData.map(item => item.interchange_number).filter(Boolean))];
      //   const uniquePartName = [...new Set(finalisedData.map(item => item.part_name).filter(Boolean))];
      //   setOverrideRegions(uniqueRegions);
      //   setOverrideInterchanges(uniqueInterchanges);
      //   setOverridePartTypes(uniquePartName);

      //   // Use these lists to seed current selections/options without breaking existing selections
      //   if (uniqueRegions.length > 0) {
      //     setRegions(uniqueRegions);
      //     if (!selectedRegion) setSelectedRegion(uniqueRegions[0]);
      //   }
      // }

      setLoading(false);
      console.log('Analysis page data loaded successfully');
      
    } catch (error) {
      console.error('Error loading Analysis page data:', error);
      setLoading(false);
    }
  };

  const populateGlobalConfig = async () => {
        // Skip if price lines were already set from history
        if (priceLinesSetFromHistory.current) 
          {
            priceLinesSetFromHistory.current = false
            return;
          }  
          
          if (selectedRegion && selectedInterchange && allStockData.length > 0 && priceLineConfig.length > 0) {
            const matchingStock = allStockData.filter(item => 
              item.region === selectedRegion && 
              item.interchange_number === selectedInterchange
            );
      
            console.log('Matching Stock Objects:', matchingStock);
            console.log('Total Matching Stock Objects:', matchingStock.length);
      
            let matchingQuartiles = [];
            if (matchingStock.length > 0) {
              const firstStockObj = matchingStock[0];
              const activePartType = (selectedPartType || 'ENGINE');
              matchingQuartiles = priceLineConfig.filter(
                config => config.stock_status === firstStockObj.stock_status &&
                          String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
              );
            }
            
            // If no matching stock or no quartiles found, default to 'Adequately Stocked'
            if (matchingQuartiles.length < 4) {
              const activePartType = (selectedPartType || 'ENGINE');
              matchingQuartiles = priceLineConfig.filter(
                config => config.stock_status === 'Adequately Stocked' &&
                          String(config.part_type).toUpperCase() === String(activePartType).toUpperCase()
              );
            }
            
            // Sort and set price line values
            if (matchingQuartiles.length >= 4) {
              const sortedQuartiles = matchingQuartiles.sort((a, b) => {
                const order = { 'Q1': 0, 'Q2': 1, 'Q3': 2, 'Q4': 3 };
                return order[a.quartile_name] - order[b.quartile_name];
              });
              setPriceLine1(sortedQuartiles[0]?.quartile_value || 0);
              setPriceLine2(sortedQuartiles[1]?.quartile_value || 0);
              setPriceLine3(sortedQuartiles[2]?.quartile_value || 0);
              setPriceLine4(sortedQuartiles[3]?.quartile_value || 0);
              setUserMileage('');
              setAdditionalPriceLine('0');
                
              console.log('Updated Price Line Values:', {
                Q1: sortedQuartiles[0]?.quartile_value,
                Q2: sortedQuartiles[1]?.quartile_value,
                Q3: sortedQuartiles[2]?.quartile_value,
                Q4: sortedQuartiles[3]?.quartile_value
              });
            }
          }
  }

  // Filter analysis based on search
  const filteredAnalysis = allAnalysis.filter(analysis =>
    analysis.analysisName.toLowerCase().includes(analysisSearch.toLowerCase())
  );

  ////////////////////////////////////////
  // EVENT HANDLERS - User Interaction Functions
  ////////////////////////////////////////
  const handleAnalysisSearchChange = (e) => {
    const value = e.target.value;
    setAnalysisSearch(value);
    setShowAnalysisDropdown(true);
    if (!value) {
      setShowAnalysisDropdown(false);
    }
  };

  // Handle analysis selection
  const handleAnalysisSelect = (analysis) => {
    setSelectedAnalysis(analysis);
    setAnalysisSearch(analysis.analysisName);
    setShowAnalysisDropdown(false);
    loadAnalysisData(analysis);
  };

  // Handle view button click from Sold Parts table
  const handleViewClick = (saleData) => {
    console.log('View clicked for sale:', saleData);
    setSelectedSale(saleData);
    setActiveTab('sales detail');
  };

  // Handle view button click from Filtered Inventory table
  const handleInventoryViewClick = (inventoryData) => {
    console.log('View clicked for inventory:', inventoryData);
    setSelectedInventory(inventoryData);
    setActiveTab('part detail');
  };

  // Load analysis data
  const loadAnalysisData = (analysis) => {
    if (analysis.region) {
      setSelectedRegion(analysis.region);
    }
    if (analysis.interchange) {
      setSelectedInterchange(analysis.interchange);
    }
    if (analysis.price_Line_Q1 !== undefined) {
      setPriceLine1(Number(analysis.price_Line_Q1));
    }
    if (analysis.price_Line_Q2 !== undefined) {
      setPriceLine2(Number(analysis.price_Line_Q2));
    }
    if (analysis.price_Line_Q3 !== undefined) {
      setPriceLine3(Number(analysis.price_Line_Q3));
    }
    if (analysis.price_Line_Q4 !== undefined) {
      setPriceLine4(Number(analysis.price_Line_Q4));
    }
    if (analysis.additional_Price_Line_Percent !== undefined) {
      setAdditionalPriceLine(Number(analysis.additional_Price_Line_Percent));
    }
    if (analysis.user_Mileage !== undefined) {
      setUserMileage(analysis.user_Mileage.toString());
    }
    if (analysis.show_Sales !== undefined) {
      setShowSoldParts(Boolean(analysis.show_Sales));
    }
    if (analysis.include_Brokered !== undefined) {
      setIncludeBrokered(Boolean(analysis.include_Brokered));
    }
    if (analysis.move_To_Price_Line !== undefined) {
      setMoveToPriceLine(Boolean(analysis.move_To_Price_Line));
    }
  };


  // Filter inventory based on selected region and interchange
  // useEffect(() => {
  //   if (selectedRegion && selectedInterchange && allInventoryData.length > 0) {
  //     const filtered = allInventoryData.filter(item => 
  //       item.region === selectedRegion && item.interchange_number === selectedInterchange && item.part_name === selectedPartType && item.mileage != 0 && item.mileage != 999 
     
  //     ).map(item => ({
  //       ...item,
  //       mileage: item.mileage * 1000  // Multiply by 1000 to match sales data scale
  //     }));
      
  //     setFilteredInventory(filtered);
  //   } else {
  //     setFilteredInventory([]);
  //   }
  // }, [selectedRegion, selectedInterchange, allInventoryData]);

  // Filter sales based on selected part type, region and interchange
  useEffect(() => {
    if (selectedPartType && selectedRegion && selectedInterchange && allSalesData.length > 0) {
      let filtered = allSalesData.filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange &&
        item.part_name === selectedPartType &&
        item.mileage != 0 && item.mileage != 999000 && item.all_parts_gross_sales !== null && item.all_parts_gross_sales !== undefined
      );

      if(filtered.length < 5){
        filtered = allSalesData.filter(item => 
          item.interchange_number === selectedInterchange &&
          item.part_name === selectedPartType &&
          item.mileage != 0 && item.mileage != 999000 && item.all_parts_gross_sales !== null && item.all_parts_gross_sales !== undefined
        );
  
      }
      
      // Don't filter by brokered here - keep all sales data for regression calculation
      
      setFilteredSales(filtered);
      console.log('Filtered Sales Data:', filtered);
    } else {
      setFilteredSales([]);
    }
  }, [selectedPartType, selectedRegion, selectedInterchange, allSalesData]);

  // Filter inventory based on selected part type, region and interchange
  useEffect(() => {
    if (selectedPartType && selectedRegion && selectedInterchange && allInventoryData.length > 0) {
      const filtered = allInventoryData.filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange &&
        item.part_name === selectedPartType
      ).map(item => ({
        ...item,
        mileage: item.mileage * 1000  // Multiply by 1000 to match sales data scale
      }));
      
      setFilteredInventory(filtered);
      console.log('Filtered Inventory Data:', filtered);
    } else {
      setFilteredInventory([]);
    }
  }, [selectedPartType, selectedRegion, selectedInterchange, allInventoryData]);

  // Quartile calculation function (same as Model Exploration)
  function getQuartiles(data) {
    if (!data || data.length === 0) return [0, 0, 0, 0, 0];
    const sorted = data.map(d => d.mileage).sort((a, b) => a - b);
    const q = p => {
      const pos = (sorted.length - 1) * p;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
      } else {
        return sorted[base];
      }
    };
    return [q(0), q(0.25), q(0.5), q(0.75), q(1)];
  }

  // Calculate mileage quartiles from sales data (same logic as Model Exploration)
  let MileageFromSalesData = [];
  if (allSalesData.length > 0 && selectedRegion && selectedInterchange) {
    MileageFromSalesData = allSalesData
      .filter(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange
      )
      .map(item => ({ mileage: item.mileage }));
  }
  const quartiles = getQuartiles(MileageFromSalesData);

  // Price line labels (same as Model Exploration)
  const priceLineLabels = [
    `Price Line % (${quartiles[0].toLocaleString()}-${quartiles[1].toLocaleString()} mi)`,
    `Price Line % (${quartiles[1].toLocaleString()}-${quartiles[2].toLocaleString()} mi)`,
    `Price Line % (${quartiles[2].toLocaleString()}-${quartiles[3].toLocaleString()} mi)`,
    `Price Line % (${quartiles[3].toLocaleString()}+ mi)`
  ];


  // Get interchanges for selected region
  useEffect(() => {
    if (selectedRegion && allSalesData.length > 0) {
      const regionInterchanges = [...new Set(
        allSalesData
          .filter(item => item.region === selectedRegion)
          .map(item => item.interchange_number)
      )];
      setInterchanges(regionInterchanges);
    }
  }, [selectedRegion, allSalesData]);

  // Filter interchanges based on search
  const filteredInterchanges = interchanges.filter(interchange =>
    interchange.toString().toLowerCase().includes(interchangeSearch.toLowerCase())
  );

  // Create interchange options (same as Model Exploration)
  const interchangeOptions = interchanges.map(interchange => ({ value: interchange, label: interchange }));

  // Update dashboard card data based on selected part type, region, and interchange
  useEffect(() => {
    if (selectedPartType && selectedRegion && selectedInterchange && allStockData.length > 0) {
      const matchingStock = allStockData.find(item => 
        item.region === selectedRegion && 
        item.interchange_number === selectedInterchange &&
        item.part_name === selectedPartType
      );

      if (matchingStock) {
        console.log('Matching Stock Data for Dashboard:', matchingStock);
        
        // Update dashboard card data
        setStockStatus(matchingStock.stock_status || '');
        setInStockCount(matchingStock.number_of_parts_in_stock_a_b_only || 0);
        setTargetQOH(matchingStock.instock_brokered_qty_sold_last_90 || 0);
        setInstockPartsNeeded(matchingStock.number_of_instock_parts_needed || 0);
        setSelectedStockData(matchingStock);
      } else {
        // Reset to default values if no match found
        setStockStatus('');
        setInStockCount(0);
        setTargetQOH(0);
        setInstockPartsNeeded(0);
        setSelectedStockData(null);
      }
    } else {
      // Reset to default values if selections are incomplete
      setStockStatus('');
      setInStockCount(0);
      setTargetQOH(0);
      setInstockPartsNeeded(0);
      setSelectedStockData(null);
    }
  }, [selectedPartType, selectedRegion, selectedInterchange, allStockData]);

  // Check if any of the 7 APIs are still loading
  const isAnyApiLoading = enginePricingLoading || 
                         transmissionPricingLoading || 
                         salesLoading || 
                         inventoryLoading || 
                         stockLoading || 
                         priceLineConfigLoading || 
                         controlsHistoryLoading;

  if (loading || isAnyApiLoading) {
    return <Loader />;
  }

  ////////////////////////////////////////
  // UI RENDER - Analysis Page Interface
  ////////////////////////////////////////
  return (
    <AnalysisSelectionContext.Provider value={{
      selectedRegion,
      selectedInterchange,
      selectedPartType,
      showSoldParts,
      includeBrokered
    }}>
      <AnalysisFilteredInventoryContext.Provider value={{ filteredInventory, setFilteredInventory }}>
        <AnalysisFilteredSalesContext.Provider value={filteredSales}>
          <AnalysisAllSalesDataContext.Provider value={allSalesData}>
            <AnalysisAllInventoryDataContext.Provider value={allInventoryData}>
              <AnalysisSuggestedPriceContext.Provider value={{ suggestedPrice, setSuggestedPrice }}>
                <div className="analysis-container">
          {/* Top Header Section */}
          {/* <div className="top-header-section">
            <div className="search-bar-container">
              <span className="search-text">Search here to get your previous Analysis</span>
              <div className="filter-input-container">
                <input 
                  type="text" 
                  placeholder="Filter by: Search Analysis" 
                  className="filter-input"
                  value={analysisSearch}
                  onChange={handleAnalysisSearchChange}
                />
                {showAnalysisDropdown && filteredAnalysis.length > 0 && (
                  <div className="analysis-dropdown">
                    {filteredAnalysis.map((analysis, index) => (
                      <div 
                        key={index}
                        className="analysis-dropdown-item"
                        onClick={() => handleAnalysisSelect(analysis)}
                      >
                        {analysis.analysisName}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div> */}

                  {/* Main Content Area */}
                  <div className="main_page_blk d-flex align-items-start">
                    <aside className="control_sidemenu_blk">
                      <div className="contorl_blk d-flex align-items-center">
                        <img src={sliderIcon} alt="Controls" className="mr_5" />
                        <span>Controls</span>
                      </div>
                      <div className="aside_items_container">
                        <div className="d-flex align-items-center justify-content-between mb_15">
                          <button className="clear_btn" onClick={clearControlsHistory}>Clear</button>
                          <button 
                            className="clear_btn save_btn" 
                            onClick={saveControlsHistory}
                            disabled={(() => {
                              // Disable save if insufficient data for meaningful analysis
                              if (!selectedPartType || !selectedRegion || !selectedInterchange) return true;
                              
                              // Check if we have enough sales data for regression (need at least 5 points)
                              const salesDataForRegression = (filteredSales || [])
                                .map(s => ({ mileage: Number(s.mileage), price: Number(s.all_parts_gross_sales) }))
                                .filter(d => Number.isFinite(d.mileage) && d.mileage > 0 && Number.isFinite(d.price) && d.price > 0);
                              
                              return salesDataForRegression.length < 5;
                            })()}
                          >
                            Save
                          </button>
                        </div>
                       
                        <label className="font_14 fw-semi text_white mb_5 d-block">Select Part Type</label>
                        <div className="position-relative mb_30">
                          <select 
                            value={selectedPartType} 
                            onChange={(e) => setSelectedPartType(e.target.value)}
                            className="form-control w-100"
                          >
                            <option value="ENGINE">Engine</option>
                            <option value="TRANSMISSION">Transmission</option>
                          </select>
                        </div>

                        <label className="font_14 fw-semi text_white mb_5 d-block">Select Region</label>
                        <div className="position-relative mb_30">
                          <select 
                            value={selectedRegion} 
                            onChange={(e) => setSelectedRegion(e.target.value)}
                            className="form-control w-100"
                          >
                            <option value="">Select Region</option>
                            {regions.map(region => (
                              <option key={region} value={region}>{region}</option>
                            ))}
                          </select>
                        </div>
                        
                        <label className="font_14 fw-semi text_white mb_5 d-block">Select Interchange</label>
                        <div className="position-relative mb_30">
                          <Select
                            options={(interchanges).map(ic => ({ value: ic, label: ic }))}
                            value={(interchanges).map(ic => ({ value: ic, label: ic })).find(opt => opt.value === selectedInterchange) || null}
                            onChange={handleInterchangeChange}
                            placeholder=""
                            isClearable
                            isDisabled={!selectedRegion}
                            classNamePrefix="react-select"
                          />
                        </div>

                        <div className="mb_15">
                          <label className="d-flex align-items-center text_white font_14">
                            <input 
                              type="checkbox" 
                              checked={showSoldParts}
                              onChange={(e) => setShowSoldParts(e.target.checked)}
                              className="mr_10"
                            />
                            Show Sold Parts on Chart
                          </label>
                        </div>

                        <div className="mb_15">
                          <label className="d-flex align-items-center text_white font_14">
                            <input 
                              type="checkbox" 
                              checked={includeBrokered}
                              onChange={(e) => setIncludeBrokered(e.target.checked)}
                              className="mr_10"
                            />
                            Include Brokered Sales
                          </label>
                        </div>

                        <label className="font_14 fw-semi text_white mb_5 d-block">Enter Mileage (mi)</label>
                        <div className="position-relative mb_30">
                          <input 
                            type="text" 
                            value={userMileage}
                            onChange={(e) => setUserMileage(e.target.value)}
                            className="form-control w-100"
                          />
                        </div>

                        <label className="font_14 fw-semi text_white mb_5 d-block">Additional Price Line % (Add to Quartile %)</label>
                        <div className="position-relative mb_30">
                          <input 
                            type="number" 
                            value={additionalPriceLine}
                            onChange={(e) => setAdditionalPriceLine(Number(e.target.value))}
                            className="form-control w-100"
                          />
                        </div>

                        <div className="mb_15">
                          <label className="d-flex align-items-center text_white font_14">
                            <input 
                              type="checkbox" 
                              checked={moveToPriceLine}
                              onChange={(e) => setMoveToPriceLine(e.target.checked)}
                              className="mr_10"
                            />
                            Move Inventory to Price Line
                          </label>
                        </div>

                        {/* <button className="primary_btn w-100">Queue Price Changes</button> */}

                        {/* Price Line Configuration in Sidebar */}
                        <div className="mt_21">
                          <div className="mb_15">
                            <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[0]}:</label>
                            <input 
                              type="number" 
                              value={priceLine1}
                              onChange={(e) => setPriceLine1(Number(e.target.value))}
                              className="form-control w-100"
                            />
                          </div>
                          <div className="mb_15">
                            <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[1]}:</label>
                            <input 
                              type="number" 
                              value={priceLine2}
                              onChange={(e) => setPriceLine2(Number(e.target.value))}
                              className="form-control w-100"
                            />
                          </div>
                          <div className="mb_15">
                            <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[2]}:</label>
                            <input 
                              type="number" 
                              value={priceLine3}
                              onChange={(e) => setPriceLine3(Number(e.target.value))}
                              className="form-control w-100"
                            />
                          </div>
                          <div className="mb_15">
                            <label className="font_14 fw-semi text_white mb_5 d-block">{priceLineLabels[3]}:</label>
                            <input 
                              type="number" 
                              value={priceLine4}
                              onChange={(e) => setPriceLine4(Number(e.target.value))}
                              className="form-control w-100"
                            />
                          </div>
                          <button className="primary_btn w-100" onClick={populateGlobalConfig}>Reset to Global Defaults</button>
                        </div>
                      </div>
                    </aside>

                    {/* Right Panel - Pricing Curve */}
                    <div className="control_main_item_container">
                      {/* AESOP Pricing App Bar - Inside Right Panel */}
                      <div className="aesop-pricing-bar">
                        <div className="app-title">
                          <span className="aesop-logo">AESOP</span>
                          <span className="pricing-app-text">Pricing App</span>
                        </div>
                      </div>
                      
                      {/* Navigation Tabs Bar - Below AESOP Pricing App */}
                      <div className="navigation-tabs-bar">
                        <div className="tabs-container">
                          <div 
                            className={`tab ${activeTab === 'main' ? 'active' : ''}`}
                            onClick={() => setActiveTab('main')}
                          >
                           <span className="font_18 "> Main</span>
                          </div>
                          <div 
                            className={`tab ${activeTab === 'part detail' ? 'active' : ''}`}
                            onClick={() => setActiveTab('part detail')}
                          >
                            <span className="font_18 "> Part Detail</span>
                          </div>
                          <div 
                            className={`tab ${activeTab === 'sales detail' ? 'active' : ''}`}
                            onClick={() => setActiveTab('sales detail')}
                          >
                            <span className="font_18 "> Sales Detail</span>
                          </div>
                          {/* <div className="tab">all interchanges</div>
                          <div className="tab">price line % config</div> */}
                        </div>
                      </div>

                      {/* Conditional Content Based on Active Tab */}
                      {activeTab === 'main' && (
                        <>
                          {/* Dashboard Cards Section - Only in Main Tab */}
                          <div className="dashboard-cards-section">
                            <div className="dashboard-cards-container">
                              {/* Card 1: Stock Status */}
                              <div className="dashboard-card stock-status-card">
                                <div className="card-icon">
                                  <div className="warehouse-icon"></div>
                                </div>
                                <div className="card-content">
                                  <div className="card-main-text">{stockStatus}</div>
                                  <div className="card-sub-text">Stock Status</div>
                                </div>
                              </div>

                              {/* Card 2: In Stock Count */}
                              <div className="dashboard-card in-stock-card">
                                <div className="card-icon">
                                  <div className="boxes-icon"></div>
                                </div>
                                <div className="card-content">
                                  <div className="card-main-text">{inStockCount}</div>
                                  <div className="card-sub-text">In Stock (A+B only)</div>
                                </div>
                              </div>

                              {/* Card 3: Target QOH */}
                              <div className="dashboard-card target-qoh-card">
                                <div className="card-icon">
                                  <div className="target-icon"></div>
                                </div>
                                <div className="card-content">
                                  <div className="card-main-text">{targetQOH}</div>
                                  <div className="card-sub-text">Target QOH (90-day sales)</div>
                                </div>
                              </div>

                              {/* Card 4: Instock Parts Needed */}
                              <div className="dashboard-card instock-needed-card">
                                <div className="card-icon">
                                  <div className="warning-icon"></div>
                                </div>
                                <div className="card-content">
                                  <div className="card-main-text">{instockPartsNeeded}</div>
                                  <div className="card-sub-text">Instock Parts Needed</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Above graph commented out as requested */}
                          {/* <PricingCurvePage /> */}
                          
                          {/* Pricing Curve Header */}
                          <div className="pricing-curve-header">
                            <div className="pricing-curve-title">Pricing Curve</div>
                          </div>
                          
                          
                          <div className="pricing-curve-content">
                            {(() => {
                              // Check if all required fields are selected
                              const isAllFieldsSelected = selectedPartType && selectedRegion && selectedInterchange;
                              
                              if (!isAllFieldsSelected) {
                                // Determine which field is missing and show appropriate message
                                let title = '';
                                let message = '';
                                
                                if (!selectedRegion) {
                                  title = 'Please select a Region';
                                  message = 'Select a region to continue';
                                } else if (!selectedInterchange) {
                                  title = 'Please select an Interchange Number';
                                  message = 'Select an interchange number to continue';
                                } else {
                                  title = 'Please select all required fields';
                                  message = 'Select part type, region, and interchange to continue';
                                }
                                
                                return (
                                  <div className="validation-popup-overlay">
                                    <div className="validation-popup">
                                      <div className="validation-popup-content">
                                        <div className="validation-popup-title">{title}</div>
                                        <div className="validation-popup-message">{message}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              // Note: Using filteredInventory and filteredSales directly instead of graphData
                              // These are already filtered by selectedPartType, selectedRegion, selectedInterchange

                              // Prepare inventory data from filteredInventory (already filtered by part type, region, interchange)
                              const inventoryData = (filteredInventory || [])
                                .map(i => ({
                                  mileage: Number(i.mileage),
                                  price: Number(i.part_price),
                                  region: i.region,
                                  interchange_number: i.interchange_number,
                                  part_name: i.part_name,
                                  days_instock: i.days_instock,
                                  stock_number: i.stock_number,
                                  unique: i.unique,
                                  model_name: i.model_name,
                                  model_year: i.model_year
                                }))
                                .filter(d => Number.isFinite(d.mileage) && d.mileage > 0 && Number.isFinite(d.price) && d.price > 0);

                              // Use all sales data for regression calculation (not filtered by brokered)
                              const allSalesDataForRegression = (filteredSales || [])
                                    .map(s => ({ mileage: Number(s.mileage), price: Number(s.all_parts_gross_sales) }))
                                    .filter(d => Number.isFinite(d.mileage) && d.mileage > 0 && Number.isFinite(d.price) && d.price > 0);

                              // Filter sales data for cross plots display based on includeBrokered checkbox
                              const salesDataForDisplay = (filteredSales || [])
                                    .filter(s => includeBrokered ? true : s.part_source !== 'Brokered')
                                    .map(s => ({ 
                                      mileage: Number(s.mileage), 
                                      price: Number(s.all_parts_gross_sales),
                                      part_source: s.part_source,  // Keep part_source for color filtering
                                      region: s.region,
                                      invoice_number: s.invoice_number,
                                      detail_id: s.detail_id,
                                      invoice_date: s.invoice_date,
                                      days_instock: s.days_instock
                                    }))
                                    .filter(d => Number.isFinite(d.mileage) && d.mileage > 0 && Number.isFinite(d.price) && d.price > 0);

                              // Regression from sales (use all sales data, not filtered by brokered)
                              const regression = (() => {
                                const pts = allSalesDataForRegression;
                                if (!pts || pts.length < 5) return null;
                                let sumX = 0, sumLogY = 0, sumXLogY = 0, sumX2 = 0, n = 0;
                                for (const p of pts) {
                                  if (p.price > 0) {
                                    const logY = Math.log(p.price);
                                    sumX += p.mileage; sumLogY += logY; sumXLogY += p.mileage * logY; sumX2 += p.mileage * p.mileage; n += 1;
                                  }
                                }
                                const denom = n * sumX2 - sumX * sumX;
                                if (n < 2 || denom === 0) return null;
                                const b = (n * sumXLogY - sumX * sumLogY) / denom;
                                const a = Math.exp((sumLogY - b * sumX) / n);
                                if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0) return null;
                                return { a, b };
                              })();

                              // Quartiles from sales mileage (use all sales data for quartiles)
                              const quartiles = (() => {
                                const arr = allSalesDataForRegression.map(s => s.mileage).sort((x, y) => x - y);
                                if (arr.length === 0) return [0,0,0,0,0];
                                const q = p => {
                                  const pos = (arr.length - 1) * p; const base = Math.floor(pos); const rest = pos - base;
                                  return arr[base + 1] !== undefined ? arr[base] + rest * (arr[base + 1] - arr[base]) : arr[base];
                                };
                                return [q(0), q(0.25), q(0.5), q(0.75), q(1)];
                              })();

                              const getPriceLinePercentForMileage = (m) => {
                                if (m <= quartiles[1]) return Number(priceLine1) + Number(additionalPriceLine);
                                if (m <= quartiles[2]) return Number(priceLine2) + Number(additionalPriceLine);
                                if (m <= quartiles[3]) return Number(priceLine3) + Number(additionalPriceLine);
                                return Number(priceLine4) + Number(additionalPriceLine);
                              };

                              // Traces
                              const traces = [];
                              if (inventoryData.length) {
                                // Calculate inventory prices and additional data for tooltips
                                const inventoryWithCalculations = inventoryData.map(d => {
                                  const originalPrice = d.price;
                                  let revisedPrice, predictedPrice;
                                  
                                  if (moveToPriceLine && regression) {
                                    // Move inventory to price line: calculate price line value for this mileage
                                    const basePrice = regression.a * Math.exp(regression.b * d.mileage);
                                    const priceLinePercent = getPriceLinePercentForMileage(d.mileage);
                                    revisedPrice = basePrice * (1 + priceLinePercent / 100);
                                    // Predicted Price is the base price without price line percentages
                                    predictedPrice = basePrice;
                                  } else {
                                    // Use original inventory price
                                    revisedPrice = d.price;
                                    // For non-moveToPriceLine case, predicted price is the same as revised price
                                    predictedPrice = d.price;
                                  }
                                  
                                  return {
                                    ...d,
                                    originalPrice,
                                    revisedPrice,
                                    predictedPrice
                                  };
                                });

                                // Calculate days in stock values for color mapping
                                const daysInStockValues = inventoryData.map(d => d.days_instock || 0);
                                const minDays = Math.min(...daysInStockValues);
                                const maxDays = Math.max(...daysInStockValues);
                                
                                traces.push({
                                  x: inventoryData.map(d => d.mileage),
                                  y: inventoryWithCalculations.map(d => d.revisedPrice),
                                  mode: 'markers',
                                  type: 'scatter',
                                  name: `Revised Price (${selectedRegion || ''})`,//${moveToPriceLine ? ' - Moved to Price Line' : ''}`,
                                  marker: { 
                                    color: daysInStockValues,
                                    colorscale: [
                                      [0, 'green'],      // 0 days = green
                                      [0.25, 'lightgreen'],
                                      [0.5, 'orange'],
                                      [1, 'red']         // 365 days = red
                                    ],
                                    cmin: 0,
                                    cmax: 365,
                                    size: 12, 
                                    symbol: 'circle', 
                                    opacity: 0.85,
                                    line: { 
                                      color: 'white', 
                                      width: 2 
                                    },
                                    showscale: true,
                                    colorbar: {
                                      title: '',
                                      titleside: 'right',
                                      tickvals: [0, 50, 100, 150, 200, 250, 300, 365],
                                      ticktext: ['0', '50', '100', '150', '200', '250', '300', '=>365'],
                                      len: 0.5,
                                      y: 0.75,
                                      yanchor: 'middle',
                                      outlinewidth: 1,
                                      outlinecolor: 'black',
                                      thickness: 30,
                                      x: 1.05
                                    }
                                  },
                                  hovertemplate: moveToPriceLine 
                                    ? 'Region: %{customdata[2]}<br>Stock #: %{customdata[3]}<br>Unique ID: %{customdata[4]}<br>Mileage: %{x:,.0f} mi<br>Original Price: $%{customdata[0]:,.0f}<br>Revised Price: $%{y:,.0f}<br>Predicted Price: $%{customdata[1]:,.0f}<br>Days In Stock: %{customdata[5]}<extra></extra>'
                                    : 'Region: %{customdata[2]}<br>Stock #: %{customdata[3]}<br>Unique ID: %{customdata[4]}<br>Mileage: %{x:,.0f} mi<br>Price: $%{y:,.0f}<br>Days In Stock: %{customdata[5]}<extra></extra>',
                                  customdata: inventoryWithCalculations.map(d => [
                                    d.originalPrice,
                                    d.predictedPrice,
                                    d.region,
                                    d.stock_number,
                                    d.unique,
                                    d.days_instock
                                  ])
                                });
                              }
                              if (showSoldParts && salesDataForDisplay.length) {
                                // Separate instock and brokered sales for different colors
                                const instockSales = salesDataForDisplay.filter(d => d.part_source !== 'Brokered');
                                const brokeredSales = salesDataForDisplay.filter(d => d.part_source === 'Brokered');

                                // Instock sales (red X markers)
                                if (instockSales.length > 0) {
                                  traces.push({
                                    x: instockSales.map(d => d.mileage),
                                    y: instockSales.map(d => d.price),
                                    mode: 'markers',
                                    type: 'scatter',
                                    name: 'Sold Price (Instock)',
                                    marker: { 
                                      color: '#d62728', 
                                      size: 12, 
                                      symbol: 'x',
                                      opacity: 0.85,
                                      line: { 
                                        color: 'white', 
                                        width: 2 
                                      } 
                                    },
                                    hovertemplate: 'Region: %{customdata[0]}<br>Invoice #: %{customdata[1]}<br>Detail ID: %{customdata[2]}<br>Mileage: %{x:,.0f} mi<br>Price: $%{y:,.0f}<br>Part Source: %{customdata[3]}<br>Sale Date: %{customdata[4]}<br>Days In Stock: %{customdata[5]}<extra></extra>',
                                    customdata: instockSales.map(d => [
                                      d.region,
                                      d.invoice_number,
                                      d.detail_id,
                                      d.part_source,
                                      d.invoice_date ? new Date(d.invoice_date).toLocaleDateString() : 'N/A',
                                      d.days_instock
                                    ])
                                  });
                                }

                                // Brokered sales (black X markers)
                                if (brokeredSales.length > 0) {
                                  traces.push({
                                    x: brokeredSales.map(d => d.mileage),
                                    y: brokeredSales.map(d => d.price),
                                    mode: 'markers',
                                    type: 'scatter',
                                    name: 'Sold Price (Brokered)',
                                    marker: { 
                                      color: '#000000', 
                                      size: 12, 
                                      symbol: 'x',
                                      opacity: 0.85,
                                      line: { 
                                        color: 'white', 
                                        width: 2 
                                      } 
                                    },
                                    hovertemplate: 'Region: %{customdata[0]}<br>Invoice #: %{customdata[1]}<br>Detail ID: %{customdata[2]}<br>Mileage: %{x:,.0f} mi<br>Price: $%{y:,.0f}<br>Part Source: %{customdata[3]}<br>Sale Date: %{customdata[4]}<br>Days In Stock: %{customdata[5]}<extra></extra>',
                                    customdata: brokeredSales.map(d => [
                                      d.region,
                                      d.invoice_number,
                                      d.detail_id,
                                      d.part_source,
                                      d.invoice_date ? new Date(d.invoice_date).toLocaleDateString() : 'N/A',
                                      d.days_instock
                                    ])
                                  });
                                }
                              }
                              if (regression) {
                                const allMiles = [...inventoryData.map(d => d.mileage), ...allSalesDataForRegression.map(d => d.mileage)];
                                const minX = allMiles.length ? Math.min(...allMiles) : 40000;
                                const maxX = allMiles.length ? Math.max(...allMiles) : 200000;
                                // Exponential fit (solid line)
                                const fitX = []; const fitY = [];
                                for (let x = minX; x <= maxX; x += 2000) { fitX.push(x); fitY.push(regression.a * Math.exp(regression.b * x)); }
                                traces.push({ 
                                  x: fitX, 
                                  y: fitY, 
                                  mode: 'lines', 
                                  type: 'scatter', 
                                  name: 'Exponential Fit (Sales)', 
                                  line: { 
                                    color: '#ff7f0e', 
                                    width: 4, 
                                    dash: 'solid' 
                                  },
                                  hovertemplate: '<b>Regression</b><br>Mileage: %{x:,.0f}<br>Predicted: $%{y:,.2f}<extra></extra>'
                                });
                                // Price Line % (dashed line)
                                const plX = []; const plY = [];
                                for (let x = minX; x <= maxX; x += 1000) { const base = regression.a * Math.exp(regression.b * x); const pct = getPriceLinePercentForMileage(x); plX.push(x); plY.push(base * (1 + pct / 100)); }
                                traces.push({ 
                                  x: plX, 
                                  y: plY, 
                                  mode: 'lines', 
                                  type: 'scatter', 
                                  name: 'Price Line %', 
                                  line: { 
                                    color: '#ff9900', 
                                    width: 3, 
                                    dash: 'dash' 
                                  },
                                  hovertemplate: '<b>Price Line</b><br>Mileage: %{x:,.0f}<br>Price: $%{y:,.2f}<extra></extra>'
                                });

                                // Add star marker for entered mileage if userMileage is provided
                                if (userMileage && userMileage.trim() !== '') {
                                  const enteredMileage = parseFloat(userMileage);
                                  if (!isNaN(enteredMileage) && enteredMileage > 0) {
                                    // Calculate price line value for the entered mileage
                                    const basePrice = regression.a * Math.exp(regression.b * enteredMileage);
                                    const priceLinePercent = getPriceLinePercentForMileage(enteredMileage);
                                    const priceLineValue = basePrice * (1 + priceLinePercent / 100);
                                    
                                    traces.push({
                                      x: [enteredMileage],
                                      y: [priceLineValue],
                                      mode: 'markers',
                                      type: 'scatter',
                                      name: `Entered Mileage (${enteredMileage.toLocaleString()} mi)`,
                                      marker: {
                                        color: '#ffd700',
                                        size: 20,
                                        symbol: 'star',
                                        opacity: 1,
                                        line: {
                                          color: 'white',
                                          width: 3
                                        }
                                      },
                                      hovertemplate: '<b>Entered Mileage</b><br>Mileage: %{x:,.0f} mi<br>Revised Price: $%{y:,.2f}<br>Base Regression: $%{customdata[0]:,.2f}<br>Price Line %: %{customdata[1]}%<extra></extra>',
                                      customdata: [[basePrice, priceLinePercent]]
                                    });
                                  }
                                }
                              }

                              // Axis ranges (use all sales data for proper range calculation)
                              const xs = [...inventoryData.map(d => d.mileage), ...allSalesDataForRegression.map(d => d.mileage)];
                              const ys = [...inventoryData.map(d => d.price), ...allSalesDataForRegression.map(d => d.price)];
                              let xRange = xs.length ? [Math.min(...xs) * 0.95, Math.max(...xs) * 1.05] : [40000, 200000];
                              let yRange = ys.length ? [Math.min(...ys) * 0.9, Math.max(...ys) * 1.1] : [1500, 6000];

                              return (
                                <Plot
                                  data={traces}
                                  layout={{
                                    title: { 
                                      text: selectedInterchange ? `Price Analysis - ${selectedPartType} vs Mileage - ${selectedPartType} (IC: ${selectedInterchange}) [Regions: ${selectedRegion || ''}]` : 'Select an Interchange to View Pricing Curve', 
                                      font: { size: 18 },
                                      x: 0.5,
                                      xanchor: 'center'
                                    },
                                    xaxis: { 
                                      title: { text: 'Mileage (miles)', font: { size: 14 } }, 
                                      range: xRange, 
                                      tickformat: ',d',
                                      showgrid: true,
                                      zeroline: false
                                    },
                                    yaxis: { 
                                      title: { text: 'Price ($)', font: { size: 14 } }, 
                                      range: yRange,
                                      showgrid: true,
                                      zeroline: false
                                    },
                                    margin: { t: 60, l: 60, r: 60, b: 60 },
                                    paper_bgcolor: '#fff7f0',
                                    plot_bgcolor: '#fff',
                                    legend: { 
                                      x: 1.02,
                                      y: 0.1,
                                      bgcolor: 'rgba(255,255,255,0.8)',
                                      bordercolor: '#ccc',
                                      borderwidth: 1
                                    },
                                    annotations: [
                                      {
                                        x: 1.22,
                                        y: 1.05,
                                        xref: 'paper',
                                        yref: 'paper',
                                        text: '<b>Days in stock</b>',
                                        showarrow: false,
                                        font: { size: 14 },
                                        align: 'center',
                                      }
                                    ]
                                  }}
                                  config={{ 
                                    displayModeBar: true, 
                                    displaylogo: false,
                                    showlegend: true,
                                    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
                                    toImageButtonOptions: {
                                      format: 'png',
                                      filename: 'price_analysis_chart',
                                      height: 800,
                                      width: 1200,
                                      scale: 2
                                    }
                                  }}
                                  style={{ width: '100%', height: '600px' }}
                                  useResizeHandler={true}
                                />
                              );
                            })()}
                          </div>
                          {/* Tables Section - Only for Main Tab */}
                          <div className="tables-section">
                            {/* Pricing Data and Interchange Applications Tables */}
                            <div className="row">
                              <div className="col-md-6">
                                <div className="table-container">
                                  <h5 className="table-title">Pricing Data</h5>
                                  <table className="data-table">
                                    <thead>
                                      <tr>
                                        <th>region</th>
                                        <th>interchange_number</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedRegion && selectedInterchange ? (
                                        <tr>
                                          <td>{selectedRegion}</td>
                                          <td>{selectedInterchange}</td>
                                        </tr>
                                      ) : (
                                        <tr>
                                          <td colSpan="2" className="no-data">No data available</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
            <div className="col-md-6">
              <div className="table-container">
                <h5 className="table-title">Interchange Applications</h5>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>model_year</th>
                      <th>model_name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStockData ? (
                      <tr>
                        <td>{selectedStockData.model_year}</td>
                        <td>{selectedStockData.model_name}</td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan="2" className="no-data">No data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
                            </div>

                            {/* Filtered Inventory Table */}
                            <div className="table-container">
                              <h5 className="table-title">Filtered Inventory</h5>
                              <div className={`table-responsive ${filteredInventory.length > 10 ? 'table-scrollable' : ''}`}>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>
                                        stock_number
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        unique
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        model_name
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        model_year
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        mileage
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        part_price
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        days_instock
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredInventory.length > 0 ? (
                                      filteredInventory.map((inventory, index) => (
                                        <tr key={index}>
                                          <td>{inventory.stock_number}</td>
                                          <td>{inventory.unique}</td>
                                          <td>{inventory.model_name}</td>
                                          <td>{inventory.model_year}</td>
                                          <td>{inventory.mileage}</td>
                                          <td>{inventory.part_price}</td>
                                          <td>{inventory.days_instock}</td>
                                          <td>
                                            <button 
                                              className="view-btn green"
                                              onClick={() => handleInventoryViewClick(inventory)}
                                            >
                                              View
                                            </button>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="8" className="no-data">No data available</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                        
                            {/* Sold Parts (Matching) Table */}
                            <div className="table-container">
                              <h5 className="table-title">Sold Parts (Matching)</h5>
                              <div className={`table-responsive ${filteredSales.length > 10 ? 'table-scrollable' : ''}`}>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>
                                        invoice_date
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        invoice_number
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        detail_id
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        part_source
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        mileage
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        all_parts_gross_sales
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>
                                        days_instock
                                        <span className="sort-icon">↕</span>
                                      </th>
                                      <th>action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredSales.length > 0 ? (
                                      filteredSales.map((sale, index) => (
                                        <tr key={index}>
                                          <td>{new Date(sale.invoice_date).toLocaleDateString()}</td>
                                          <td>{sale.invoice_number}</td>
                                          <td>{sale.detail_id}</td>
                                          <td>{sale.part_source}</td>
                                          <td>{sale.mileage}</td>
                                          <td>{sale.all_parts_gross_sales}</td>
                                          <td>{sale.days_instock}</td>
                                          <td>
                                            <button 
                                              className="view-btn green"
                                              onClick={() => handleViewClick(sale)}
                                            >
                                              View
                                            </button>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="8" className="no-data">No data available</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Sales Velocity Scatter Plot Section */}
                            <div className="scatter-plot-section br_0">
                              <div className="scatter-plot-container">
                                <h5 className="table-title">Sales Velocity (Days In Stock vs Price)</h5>
                                <div className="scatter-plot-content">
                                  {(() => {
                                    // Prepare data for scatter plot
                                    const scatterData = filteredSales.filter(sale => 
                                      sale.days_instock !== null && 
                                      sale.days_instock !== undefined && 
                                      sale.all_parts_gross_sales !== null && 
                                      sale.all_parts_gross_sales !== undefined &&
                                      sale.mileage !== null &&
                                      sale.mileage !== undefined
                                    );

                                    if (scatterData.length === 0) {
                                      return (
                                        <div className="no-data-message">
                                          No sales data available for scatter plot
                                        </div>
                                      );
                                    }

                                    // Create scatter plot traces
                                    const traces = [{
                                      x: scatterData.map(d => d.days_instock),
                                      y: scatterData.map(d => d.all_parts_gross_sales),
                                      mode: 'markers',
                                      type: 'scatter',
                                      name: 'Sales Data',
                                      showlegend: false,
                                      marker: {
                                        color: scatterData.map(d => d.mileage),
                                        colorscale: [
                                          [0, 'green'],      // 0 miles = green
                                          [0.25, 'lightgreen'],
                                          [0.5, 'yellow'],
                                          [0.75, 'orange'],
                                          [1, 'red']         // 150,000 miles = red
                                        ],
                                        cmin: 0,
                                        cmax: 150000,
                                        size: 12,
                                        opacity: 0.9,
                                        line: {
                                          color: 'white',
                                          width: 2
                                        },
                                        colorbar: {
                                          title: {
                                            text: 'Mileage',
                                            font: { 
                                              size: 14, 
                                              color: '#333',
                                              family: 'Arial, sans-serif'
                                            }
                                          },
                                          tickvals: [0, 20000, 40000, 60000, 80000, 100000, 120000, 140000, 150000],
                                          ticktext: ['0', '20,000', '40,000', '60,000', '80,000', '100,000', '120,000', '140,000', '=>150,000'],
                                          tickfont: {
                                            size: 12,
                                            color: '#666',
                                            family: 'Arial, sans-serif'
                                          },
                                          len: 1.0,
                                          thickness: 25,
                                          outlinewidth: 0,
                                          outlinecolor: 'transparent',
                                          bgcolor: 'transparent',
                                          bordercolor: 'transparent',
                                          borderwidth: 0
                                        }
                                      },
                                      hovertemplate: 
                                        '<b>Sale Details</b><br>' +
                                        'Days In Stock: %{x}<br>' +
                                        'Sale Price: $%{y:,.0f}<br>' +
                                        'Mileage: %{marker.color:,.0f} mi<br>' +
                                        '<extra></extra>'
                                    }];

                                    // Add highlighted band for 50-100 days
                                    const bandTrace = {
                                      x: [50, 50, 100, 100, 50],
                                      y: [
                                        Math.min(...scatterData.map(d => d.all_parts_gross_sales)) * 0.9,
                                        Math.max(...scatterData.map(d => d.all_parts_gross_sales)) * 1.1,
                                        Math.max(...scatterData.map(d => d.all_parts_gross_sales)) * 1.1,
                                        Math.min(...scatterData.map(d => d.all_parts_gross_sales)) * 0.9,
                                        Math.min(...scatterData.map(d => d.all_parts_gross_sales)) * 0.9
                                      ],
                                      mode: 'lines',
                                      fill: 'toself',
                                      fillcolor: 'rgba(135, 206, 235, 0.2)',
                                      line: { 
                                        color: 'rgba(135, 206, 235, 0.4)',
                                        width: 1,
                                        dash: 'dot'
                                      },
                                      name: '50-100 Day Band',
                                      showlegend: false,
                                      hoverinfo: 'skip'
                                    };

                                    traces.push(bandTrace);

                                    return (
                                      <Plot
                                        data={traces}
                                        layout={{
                                          title: {
                                            text: 'Sales Velocity (50-100 Day Band Highlighted)',
                                            font: { 
                                              size: 18, 
                                              color: '#333',
                                              family: 'Arial, sans-serif'
                                            },
                                            x: 0.5,
                                            xanchor: 'center',
                                            y: 0.95
                                          },
                                          xaxis: {
                                            title: { 
                                              text: 'Days In Stock Before Sold', 
                                              font: { 
                                                size: 14, 
                                                color: '#333',
                                                family: 'Arial, sans-serif'
                                              } 
                                            },
                                            showgrid: true,
                                            gridcolor: '#e0e0e0',
                                            gridwidth: 1,
                                            zeroline: false,
                                            zerolinecolor: '#e0e0e0',
                                            zerolinewidth: 1,
                                            tickfont: { 
                                              size: 12, 
                                              color: '#666',
                                              family: 'Arial, sans-serif'
                                            },
                                            range: [0, Math.max(...scatterData.map(d => d.days_instock)) * 1.1],
                                            dtick: Math.max(25, Math.ceil(Math.max(...scatterData.map(d => d.days_instock)) / 5)),
                                            tickangle: 0,
                                            tickmode: 'linear',
                                            showline: true,
                                            linecolor: '#ccc',
                                            linewidth: 1
                                          },
                                          yaxis: {
                                            title: { 
                                              text: 'Sale Price ($)', 
                                              font: { 
                                                size: 14, 
                                                color: '#333',
                                                family: 'Arial, sans-serif'
                                              } 
                                            },
                                            showgrid: true,
                                            gridcolor: '#e0e0e0',
                                            gridwidth: 1,
                                            zeroline: false,
                                            zerolinecolor: '#e0e0e0',
                                            zerolinewidth: 1,
                                            tickfont: { 
                                              size: 12, 
                                              color: '#666',
                                              family: 'Arial, sans-serif'
                                            },
                                            range: [
                                              Math.min(...scatterData.map(d => d.all_parts_gross_sales)) * 0.9,
                                              Math.max(...scatterData.map(d => d.all_parts_gross_sales)) * 1.1
                                            ],
                                            dtick: 400,
                                            showline: true,
                                            linecolor: '#ccc',
                                            linewidth: 1
                                          },
                                          margin: { t: 80, l: 80, r: 80, b: 100 },
                                          paper_bgcolor: '#f8f9fa',
                                          plot_bgcolor: '#ffffff',
                                          font: {
                                            family: 'Arial, sans-serif',
                                            color: '#333'
                                          },
                                          legend: {
                                            x: 1.02,
                                            y: 1,
                                            bgcolor: 'rgba(255,255,255,0.9)',
                                            bordercolor: '#ddd',
                                            borderwidth: 1,
                                            font: { 
                                              size: 12,
                                              family: 'Arial, sans-serif'
                                            }
                                          },
                                          annotations: []
                                        }}
                                        config={{
                                          displayModeBar: true,
                                          displaylogo: false,
                                          showlegend: true,
                                          modeBarButtonsToRemove: [],
                                          modeBarButtonsToAdd: [],
                                          toImageButtonOptions: {
                                            format: 'png',
                                            filename: 'sales_velocity_chart',
                                            height: 600,
                                            width: 1000,
                                            scale: 2
                                          },
                                          responsive: true,
                                          staticPlot: false,
                                          doubleClick: 'reset+autosize',
                                          showTips: true,
                                          showLink: false,
                                          linkText: '',
                                          locale: 'en'
                                        }}
                                        style={{ width: '100%', height: '500px' }}
                                        useResizeHandler={true}
                                      />
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      
                      {activeTab === 'part detail' && (
                        <div className="part-detail-section">
                          <div className="part-detail-container">
                            <h5 className="part-detail-title">Part Detail</h5>
                            <div className="part-detail-content">
                              {selectedInventory ? (
                                <div className="part-detail-data">
                                  <div className="part-field">
                                    <span className="field-label">comments:</span>
                                    <span className="field-value">{selectedInventory.comments || ''}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">region:</span>
                                    <span className="field-value">{selectedInventory.region}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">interchange_number:</span>
                                    <span className="field-value">{selectedInventory.interchange_number}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">mileage:</span>
                                    <span className="field-value">{selectedInventory.mileage}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">part_price:</span>
                                    <span className="field-value">{selectedInventory.part_price}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">stock_number:</span>
                                    <span className="field-value">{selectedInventory.stock_number}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">unique:</span>
                                    <span className="field-value">{selectedInventory.unique}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">model_name:</span>
                                    <span className="field-value">{selectedInventory.model_name}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">model_year:</span>
                                    <span className="field-value">{selectedInventory.model_year}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">days_instock:</span>
                                    <span className="field-value">{selectedInventory.days_instock}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">highest_core_price:</span>
                                    <span className="field-value">{selectedInventory.highest_core_price || 0}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">part_name:</span>
                                    <span className="field-value">{selectedInventory.part_name}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">keyword_flag:</span>
                                    <span className="field-value">{selectedInventory.keyword_flag || ''}</span>
                                  </div>
                                  <div className="part-field">
                                    <span className="field-label">keyword_flag_flat:</span>
                                    <span className="field-value">{selectedInventory.keyword_flag_flat || ''}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="no-data-selected">
                                  Select the data
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {activeTab === 'sales detail' && (
                        <div className="sale-detail-section">
                          <div className="sale-detail-container">
                            <h5 className="sale-detail-title">Sale Detail</h5>
                            <div className="sale-detail-content">
                              {selectedSale ? (
                                <div className="sale-detail-data">
                                  <div className="sale-field">
                                    <span className="field-label">region:</span>
                                    <span className="field-value">{selectedSale.region}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">interchange_number:</span>
                                    <span className="field-value">{selectedSale.interchange_number}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">mileage:</span>
                                    <span className="field-value">{selectedSale.mileage}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">all_parts_gross_sales:</span>
                                    <span className="field-value">{selectedSale.all_parts_gross_sales}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">part_source:</span>
                                    <span className="field-value">{selectedSale.part_source}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">instock_screen_price:</span>
                                    <span className="field-value">{selectedSale.instock_screen_price}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">customer_category:</span>
                                    <span className="field-value">{selectedSale.customer_category}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">invoice_date:</span>
                                    <span className="field-value">{new Date(selectedSale.invoice_date).toLocaleDateString()}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">invoice_number:</span>
                                    <span className="field-value">{selectedSale.invoice_number}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">detail_id:</span>
                                    <span className="field-value">{selectedSale.detail_id}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">days_instock:</span>
                                    <span className="field-value">{selectedSale.days_instock}</span>
                                  </div>
                                  <div className="sale-field">
                                    <span className="field-label">part_name:</span>
                                    <span className="field-value">{selectedSale.part_name}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="no-data-selected">
                                  Select the data
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  {/* Popups */}
                  <ErrorMessagePopup
                    isVisible={popupState.isVisible}
                    message={popupState.message}
                    subMessage={popupState.subMessage}
                    popupType={popupState.popupType}
                    customPosition={popupState.customPosition}
                    onClose={closePopup}
                  />
                </div>
              </AnalysisSuggestedPriceContext.Provider>
            </AnalysisAllInventoryDataContext.Provider>
          </AnalysisAllSalesDataContext.Provider>
        </AnalysisFilteredSalesContext.Provider>
      </AnalysisFilteredInventoryContext.Provider>
    </AnalysisSelectionContext.Provider>
  );
};

////////////////////////////////////////
// EXPORT - Analysis Page Component
////////////////////////////////////////
export default AnalysisPage;


